/**
 * S2 overview block: identity and unified state of one agent.
 * State is Draft | Private | Public | Archived (single field).
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { STUDIO_AGENT_STATES } from '@/types/aiStudioAgent';
import type { AiStudioAgent, StudioAgentState } from '@/types/aiStudioAgent';

interface AgentOverviewProps {
  agent: AiStudioAgent;
  saving: boolean;
  onChangeState: (state: StudioAgentState) => void;
}

const TERM_CLASS = 'text-base text-[var(--color-surface-text-secondary)]';
const VALUE_CLASS = 'text-base text-[var(--color-surface-text)]';

export const AgentOverview: React.FC<AgentOverviewProps> = ({
  agent,
  saving,
  onChangeState,
}) => {
  const { t } = useLanguage();

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4">
      <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.agents.detail.overview')}
      </h2>

      <p className={`mb-4 ${VALUE_CLASS}`}>
        {agent.description || t('aiStudio.agents.detail.noDescription')}
      </p>

      <dl className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className={TERM_CLASS}>{t('aiStudio.agents.columnDomain')}</dt>
          <dd className={VALUE_CLASS}>{agent.domain || t('aiStudio.common.notSet')}</dd>
        </div>
        <div>
          <dt className={TERM_CLASS}>{t('aiStudio.agents.columnScope')}</dt>
          <dd className={VALUE_CLASS}>
            {agent.isPlatform
              ? t('aiStudio.agents.scopePlatform')
              : t('aiStudio.agents.scopeTenant')}
          </dd>
        </div>
        <div>
          <dt className={TERM_CLASS}>
            <label htmlFor="agent-state">{t('aiStudio.agents.state')}</label>
          </dt>
          <dd>
            <select
              id="agent-state"
              value={agent.visibility}
              disabled={saving}
              onChange={(event) => onChangeState(event.target.value as StudioAgentState)}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
            >
              {STUDIO_AGENT_STATES.map((state) => (
                <option key={state} value={state}>
                  {t(`aiStudio.agents.state.${state}`)}
                </option>
              ))}
            </select>
          </dd>
        </div>
      </dl>
    </section>
  );
};
